import { Component, ElementRef, EventEmitter, OnDestroy, ViewChild } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subject, combineLatest, distinctUntilChanged, takeUntil, tap } from 'rxjs';
import { selectHasUpdateMoveMoney } from '../../authorize/reducers';
import { TaxWithholdingFields, TransactionFormServiceError } from '../../create-request/models/form/TransactionForm';
import { CashieringRequestApr } from '../../create-request/models/internal-transfer/CashieringRequestApr';
import { RecurringRequest } from '../../create-request/models/internal-transfer/RecurringRequest';
import { SloaRequest } from '../../create-request/models/internal-transfer/SloaRequest';
import { WarningOrError } from '../../create-request/models/shared/WarningOrError';
import { selectCreateTransactionInProgress, selectTransactionFormDirection, selectTransactionFormServiceError, selectTransactionType, selectTransactionsTableState, selectTweTableState } from '../../create-request/reducers';
import { displayCancelModal } from '../../shared/actions/shared.actions';
import { selectAcknowledgementError, selectCancelModalContinueClickedForEditTwe, selectRmdAgeInfo, selectViewInContext } from '../../shared/reducers';
import { FeatureFlagService } from '../../shared/services/feature-flag-service';
import { Hyperlink } from '../../shared/types/Hyperlink';
import { TableComponentState } from '../../shared/types/TableComponentState';
import { Constants, pageInContext, transactionDirection, transactionTypes } from '../../shared/utilities/Constants';
import { selectAchVerifyCallWarning, selectCreateStandingAuthInProgress } from '../../standing-authorizations/reducers';
import { AccountInfoActions } from '../actions';
import { AccountInfo, CashBalance, TaxInfo } from '../models';
import { Eauthorization } from '../models/Eauthorization';
import { RmdAgeInfo } from '../models/RmdAgeInfo';
import { selectCashByAccountType, selectEAuthorization, selectHeader, selectInfo, selectLoadTransactionsCallFinished, selectNraIndicator, selectOverviewFailed, selectPalBalances, selectStateCode, selectSubAccountId, selectTaxInfo, selectTaxWithholdings, selectTweRecurringTransactions, selectTweStandingAuth, selectTweUpcomingTransactions } from '../reducers';
import { initialHeaderState } from '../reducers/header-state.reducer';
import { PfaDetails } from '../../create-request/models/overview/PfaDetails';
import { InhIraAccountAbreviations } from 'projects/money-movement-ui/src/app/shared/utilities/regex-patterns';
import { HttpStatusCode } from '@angular/common/http';
import { CoreService } from '@charlesschwab/sac-ui-core';
import { ValidateWireFlagsService } from '../../shared/services/validate-WireFlags.service';
import { isPalAccountFlow } from '../../shared/utilities/AccountFlow';

@Component({
  standalone: false,
  selector: 'mm-account-info',
  templateUrl: './account-info.component.html',
  styleUrls: ['./account-info.component.scss']  
})
export class AccountInfoComponent implements OnDestroy {
  @ViewChild('account',{static:false}
    
  ) accountinfo: ElementRef;
  
  taxInfo$: Observable<TaxInfo>;
  rmdAgeInfo$: Observable<RmdAgeInfo>;
  headerState$: Observable<typeof initialHeaderState>;
  overviewFailed$: Observable<boolean | undefined>;
  destroy$ = new Subject<void>();
  cashBalances$: Observable<CashBalance[]>;
  accountInfo$: Observable<AccountInfo>;
  isNraAccount$: Observable<boolean | undefined>;
  eAuthorization$: Observable<Eauthorization>;
  isNraAccount: boolean | undefined;
  isIra: boolean;
  isPtrs: boolean;
  isRmdEligible: boolean | undefined;
  isRothAccount: boolean | undefined;
  isInhAccount: boolean | undefined;
  isNoTaxAllowedAccount: boolean | undefined;
  isInhIraAccountAdditional: boolean | undefined;
  incoming: boolean;
  isAch: boolean;
  accountId: string;
  formerTdaIndicator: boolean;
  overviewAccountInfoCallLoading: boolean;
  warningMessageList: WarningOrError[];
  achAuthorizationErrorHeader: string = Constants.achAuthorizationErrorHeader;
  noInstructionsErrorLink: Hyperlink = {
    url: '',
    text: 'create a new profile',
    target: '_blank',
    isActionCenterHyperLink: true
  }
  showEditTweUpdateModal = new EventEmitter<boolean>();
  showIRACalcModal = new EventEmitter<boolean>();
  showTweModal$: Subject<boolean> = new Subject();
  tweUpcomingTransactions$: Observable<(CashieringRequestApr | RecurringRequest)[]>;
  tweStandingAuth$: Observable<SloaRequest[]>;
  tweRecurringTransactions$: Observable<RecurringRequest[]>;
  taxWithholdings$: Observable<TaxWithholdingFields>;
  tweTableState$: Observable<TableComponentState>;
  isEditAccountLevelTwe: boolean;
  createTransactionInProgress: boolean;
  viewInContext: string;
  createStandingAuthInProgress: boolean;
  pfaDetails: PfaDetails;
  hasUpdateMoveMoney: boolean;
  transactionFormServiceError$: Observable<TransactionFormServiceError>;
  hasBadRequestServiceError: boolean = false;
  hasServiceError: boolean = false;
  hasError$: Observable<unknown>;
  hasAtpTimeoutError: boolean = false;
  serviceErrorList: string[];
  stateCode$: Observable<string>;
  transactionType: string;
  isOverViewCallFinished: boolean;
  isPalAccount: boolean = false;
  palBalances$: Observable<CashBalance[]>;
  

constructor(
    private store: Store, 
    private flags: FeatureFlagService,
    private coreService: CoreService,
    private validateWireFlagsService: ValidateWireFlagsService
  ) { }

  ngOnInit(): void {
    this.hasError$ = this.store.pipe(
      select(selectAcknowledgementError)
    );
    
    this.cashBalances$ = this.store.select(selectCashByAccountType);
    this.palBalances$ = this.store.select(selectPalBalances);

    this.isPalAccount = isPalAccountFlow(this.coreService.currentContext.contextType, this.validateWireFlagsService.enablePalWireCreateTransaction());

    this.coreService.currentContextUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((context) => {
        this.isPalAccount = isPalAccountFlow(context?.contextType, this.validateWireFlagsService.enablePalWireCreateTransaction());
      });
    
    this.headerState$ = this.store.select(selectHeader);
    this.overviewFailed$ = this.store.select(selectOverviewFailed)
    this.accountInfo$ = this.store.select(selectInfo)
      .pipe(
        tap(info => {
          this.isIra = info.classifications.isIra;
          this.isPtrs = info.classifications.isPtrs;
          this.isRmdEligible = info.classifications.isRmdEligible;
          this.formerTdaIndicator = info.formerTdaIndicator;
          this.isRothAccount = info.registrationType.includes('Roth');
          this.isInhAccount = info.registrationType.includes('Inh');
          this.isNoTaxAllowedAccount = info.registrationType.includes('Part QRP/Keo') || info.registrationType.includes('I401(k)');
          this.isInhIraAccountAdditional = InhIraAccountAbreviations.test(info.registrationType.toLowerCase());
          this.pfaDetails = info.pfaDetails;
        })
      );
    this.taxInfo$ = this.store.pipe(select(selectTaxInfo));
    this.store.select(selectLoadTransactionsCallFinished).pipe(takeUntil(this.destroy$)).subscribe(overviewCallSuccess => {
        this.isOverViewCallFinished = overviewCallSuccess;
        if(overviewCallSuccess) {        
          this.rmdAgeInfo$ = this.store.pipe(select(selectRmdAgeInfo));
        }
    })
    this.store.select(selectTransactionType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        this.isAch = val === transactionTypes.ach;
      });
    this.store.select(selectTransactionFormDirection)
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        this.incoming = val === transactionDirection.in.toLowerCase();
      });
    this.store.select(selectSubAccountId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        if (val) {
          this.accountId = val;
        }
      });
    this.isNraAccount$ = this.store.select(selectNraIndicator);

    combineLatest([
      this.store.select(selectTransactionsTableState),
      this.store.select(selectHeader),
      this.isNraAccount$,
    ])
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe(([overviewCallState, headerState, isNra]) => {
        this.isNraAccount = isNra;
        this.overviewAccountInfoCallLoading = overviewCallState.loading || headerState.loading;
      });

    this.store.dispatch(
      AccountInfoActions.updateSubAccount({ accountId: this.accountId })
    );

    this.eAuthorization$ = this.store.select(selectEAuthorization);

    this.store.select(selectAchVerifyCallWarning).pipe(takeUntil(this.destroy$)).subscribe(warningMessageList => this.warningMessageList = warningMessageList);

    this.tweUpcomingTransactions$ = this.store.select(selectTweUpcomingTransactions);
    this.tweStandingAuth$ = this.store.select(selectTweStandingAuth);
    this.tweRecurringTransactions$ = this.store.select(selectTweRecurringTransactions);
    this.taxWithholdings$ = this.store.select(selectTaxWithholdings);
    this.tweTableState$ = this.store.select(selectTweTableState);

    this.store.select(selectCreateTransactionInProgress).pipe(takeUntil(this.destroy$)).subscribe(createTransactionInProgress => {
      this.createTransactionInProgress = createTransactionInProgress;
    });

    this.store.select(selectCancelModalContinueClickedForEditTwe).pipe(takeUntil(this.destroy$)).subscribe(continueClicked => {
      if (continueClicked) {
        this.isEditAccountLevelTwe = true;
        this.showEditTweUpdateModal.emit(true);
        this.store.dispatch(AccountInfoActions.editTweLinkClicked());
      }
    });

    this.store.select(selectViewInContext).pipe(takeUntil(this.destroy$)).subscribe(viewInContext => {
      this.viewInContext = viewInContext;
    });

    this.store.select(selectCreateStandingAuthInProgress).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.createStandingAuthInProgress = !!val;
    });

    this.store.select(selectHasUpdateMoveMoney).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.hasUpdateMoveMoney = val;
    });

    this.stateCode$ = this.store.select(selectStateCode);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  refresh(accountId: string): void {
    this.store.dispatch(AccountInfoActions.refresh({ accountId }));
  }

  get achWarningMessage() {
    if (this.warningMessageList) {
      for (let i = 0; i < this.warningMessageList.length; i++) {
        if ((this.warningMessageList[i].messageField !== 'CTWE' && this.warningMessageList[i].messageField !== 'IRS_DIST_CD')) {
          return true;
        } else {
          return false;
        }
      }
    }
  }

  get shouldShowTaxWithholding() {
    return (
      this.isIra &&
      !this.isNraAccount &&
      !(this.incoming && this.isAch) &&
      !this.overviewAccountInfoCallLoading &&
      !this.isNoTaxAllowedAccount
    );
  }

  get shouldShowIraCalc() {
    return (
      this.isInhIraAccountAdditional
    );
  }

  get shouldShowRmdAgeDate() {
    return (
      this.isIra &&
      !this.isRmdEligible &&
      !this.overviewAccountInfoCallLoading &&
      !this.isRothAccount &&
      !this.isInhAccount && 
      this.isOverViewCallFinished
    );
  }

  get serviceErrorMessage(): string {
    return Constants.genericServiceError;
  }

  showEditLink(): boolean {
    return this.pfaDetails.hasWpfaRole && this.hasUpdateMoveMoney;
  }

  editTweClicked(): void {
    if (this.createTransactionInProgress && this.viewInContext !== pageInContext.confirmation) {
      this.store.dispatch(displayCancelModal({
        nextUrlState: '',
        title: 'Cancel this transaction in progress?',
        content: ['This will reset your transaction inputs. Are you sure you want to continue?'],
        greenButton: 'Cancel transaction',
        grayButton: 'Back',
        source: this.viewInContext === pageInContext.review ? 'editTweModalReviewPage' : 'editTweModalInputPage'
      }));
    } else if (this.createStandingAuthInProgress && this.viewInContext !== pageInContext.confirmation) {
      this.store.dispatch(displayCancelModal({
        nextUrlState: '',
        title: 'Cancel this work in progress?',
        content: [`The information you have entered to create this standing authorization will be lost.`,],
        greenButton: 'Cancel standing authorization',
        grayButton: 'Back',
        source: this.viewInContext === pageInContext.review ? 'editTweModalReviewPage' : 'editTweModalInputPage'
      }));
    } else {
      this.isEditAccountLevelTwe = true;
      this.showEditTweUpdateModal.emit(true);
      this.store.dispatch(AccountInfoActions.editTweLinkClicked());
    }
  }

  showTweModal(showPopup: boolean): void {
    this.showTweModal$.next(showPopup);
  }

  showIraCalcModal(): void {
    this.showIRACalcModal.emit(true);
  }
  
}
